/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Link, useParams } from 'react-router-dom'
import { useMemo, useState } from 'react'
import Loading from '@/components/Loading'
import { DataTable } from '@/components/CustomTable'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import AppDialog, { ModalActions } from '@/components/CustomModal'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from 'sonner'
import type { ColumnDef } from '@tanstack/react-table'
import {
  useContainerQuery,
  useContainerMovesQuery,
  useContainerInventoryQuery,
  useAdjustContainerStockMutation,
} from '@/hooks/useContainers'
import ItemPicker, { type PickerItem } from '@/components/ItemPicker'

type InventoryLevel = {
  qty: number
  item: { id: string; name: string; sku?: string | null }
}
type MoveRow = {
  createdAt?: string
  occurredAt?: string
  name?: 'Stocker' | 'Destocker' // compat V3 (products[].name)
  type?:
    | 'PURCHASE'
    | 'ADJUST_UP'
    | 'ADJUST_DOWN'
    | 'TRANSFER_IN'
    | 'TRANSFER_OUT'
    | 'SALE'
    | string
  quantity?: number // compat V3 (products[].quantity)
  qty?: number // compat V4 (si on normalise)
  item?: { id: string; name: string; sku?: string | null }
  note?: string | null
  unitPrice?: number | null
}

export default function ContainerDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [tab, setTab] = useState<'inventory' | 'moves'>('inventory')

  // data
  const { data: container, status } = useContainerQuery(id!, !!id)
  const { data: moves } = useContainerMovesQuery(id, !!id)
  const { data: inventory } = useContainerInventoryQuery(id, !!id)

  // ----- colonnes inventaire
  const invCols = useMemo<ColumnDef<InventoryLevel>[]>(
    () => [
      {
        accessorKey: 'item.name',
        header: 'Article',
        cell: ({ row }) => (
          <Link
            className='font-medium underline-offset-2 hover:underline'
            to={`/items/${row.original.item.id}`}
          >
            {row.original.item.name}
          </Link>
        ),
      },
      {
        accessorKey: 'item.sku',
        header: 'SKU',
        cell: ({ row }) => row.original.item.sku ?? '—',
      },
      {
        accessorKey: 'qty',
        header: 'Qté',
        cell: ({ row }) => (
          <span className='tabular-nums'>{row.original.qty}</span>
        ),
      },
      {
        id: 'quick-actions',
        header: 'Actions',
        cell: ({ row }) => (
          <div className='flex gap-2'>
            <Button
              size='sm'
              variant='outline'
              onClick={() =>
                openAdjust({
                  itemId: row.original.item.id,
                  direction: 'IN',
                })
              }
            >
              Stocker
            </Button>
            <Button
              size='sm'
              variant='outline'
              onClick={() =>
                openAdjust({
                  itemId: row.original.item.id,
                  direction: 'OUT',
                })
              }
            >
              Destocker
            </Button>
          </div>
        ),
      },
    ],
    [openAdjust],
  )

  // ----- colonnes mouvements (compat V3 ou V4)
  const moveCols = useMemo<ColumnDef<MoveRow>[]>(
    () => [
      {
        accessorKey: 'occurredAt',
        header: 'Date',
        cell: ({ row }) => {
          const d = row.original.occurredAt ?? row.original.createdAt
          return d ? new Date(d).toLocaleString() : '—'
        },
      },
      {
        accessorKey: 'type',
        header: 'Type',
        cell: ({ row }) => {
          const t = row.original.type
          const n = row.original.name
          // compat V3: name "Stocker/Destocker"
          if (n === 'Stocker') return 'Ajustement (+)'
          if (n === 'Destocker') return 'Ajustement (−)'
          return humanType(t ?? '')
        },
      },
      {
        accessorKey: 'item',
        header: 'Article',
        cell: ({ row }) =>
          row.original.item ? (
            <Link
              className='underline-offset-2 hover:underline'
              to={`/items/${row.original.item.id}`}
            >
              {row.original.item.name}
            </Link>
          ) : (
            '—'
          ),
      },
      {
        accessorKey: 'qty',
        header: 'Qté',
        cell: ({ row }) => {
          // compat V3 (quantity) ou V4 (qty + type)
          const q = row.original.quantity ?? row.original.qty ?? 0
          const s =
            row.original.name === 'Stocker'
              ? +1
              : row.original.name === 'Destocker'
                ? -1
                : signFor(row.original.type ?? '')
          const val = s * Math.abs(q)
          return (
            <span
              className={`tabular-nums ${
                val > 0 ? 'text-green-600' : val < 0 ? 'text-red-600' : ''
              }`}
            >
              {val}
            </span>
          )
        },
      },
      {
        accessorKey: 'note',
        header: 'Note',
        cell: ({ row }) => row.original.note ?? '—',
      },
    ],
    [],
  )

  // ----- lignes
  const invRows: InventoryLevel[] = useMemo(
    () =>
      (inventory?.rows ?? container?.inventoryLevels ?? []) as InventoryLevel[], // fallback si pas d’endpoint inventaire
    [inventory, container],
  )

  const moveRows: MoveRow[] = useMemo(() => {
    // V3: moves?.products (name, quantity, createdAt)
    // V4: container?.moves (type, qty, occurredAt)
    const v3 = (moves as any)?.products ?? null
    if (Array.isArray(v3)) return v3
    return ((container as any)?.moves ?? []) as MoveRow[]
  }, [moves, container])

  // ----- compteur total (V3: compte[], sinon fallback inventaire)
  const totalBal = useMemo(() => {
    const compte = (moves as any)?.compte as
      | Array<{
          _id: string
          totalQuantity: number
        }>
      | undefined
    const viaV3 = compte?.find((c) => c._id === id)?.totalQuantity
    if (typeof viaV3 === 'number') return viaV3

    // fallback : somme inventaire
    return invRows.reduce((s, l) => s + (l?.qty ?? 0), 0)
  }, [moves, invRows, id])

  // ----- modale AJUSTEMENT
  const adjustMut = useAdjustContainerStockMutation(id)
  const [adjustOpen, setAdjustOpen] = useState(false)
  const [adjust, setAdjust] = useState<{
    itemId: string
    itemName?: string
    quantity: string
    unitPrice: string
    note: string
    direction: 'IN' | 'OUT'
  }>({
    itemId: '',
    itemName: '',
    quantity: '',
    unitPrice: '',
    note: '',
    direction: 'IN',
  })

  function openAdjust(preset?: Partial<typeof adjust>) {
    setAdjust((a) => ({
      ...a,
      ...preset,
      quantity: preset?.quantity ?? '',
    }))
    setAdjustOpen(true)
  }
  async function submitAdjust() {
    if (!id) return
    const qty = parseInt(adjust.quantity || '0', 10)
    if (!adjust.itemId) return toast.error('Article requis')
    if (!qty || isNaN(qty)) return toast.error('Quantité invalide')

    const signed = adjust.direction === 'OUT' ? -Math.abs(qty) : Math.abs(qty)
    try {
      await adjustMut.mutateAsync({
        itemId: adjust.itemId,
        quantity: signed,
        unitPrice: adjust.unitPrice || '0',
        note: adjust.note || undefined,
      })
      toast.success('Mouvement enregistré')
      setAdjustOpen(false)
      setAdjust({
        itemId: '',
        quantity: '',
        unitPrice: '',
        note: '',
        direction: 'IN',
      })
    } catch (e: any) {
      toast.error('Échec', {
        description: e?.response?.data?.message ?? 'Impossible d’enregistrer',
      })
    }
  }

  // ----- helpers
  function signFor(t: string) {
    switch (t) {
      case 'PURCHASE':
      case 'ADJUST_UP':
      case 'TRANSFER_IN':
        return +1
      case 'ADJUST_DOWN':
      case 'TRANSFER_OUT':
      case 'SALE':
        return -1
      default:
        return 0
    }
  }
  function humanType(t: string) {
    const map: Record<string, string> = {
      PURCHASE: 'Entrée (Achat)',
      ADJUST_UP: 'Ajustement (+)',
      ADJUST_DOWN: 'Ajustement (−)',
      TRANSFER_IN: 'Transfert (Entrée)',
      TRANSFER_OUT: 'Transfert (Sortie)',
      SALE: 'Sortie (Vente)',
    }
    return map[t] ?? t
  }

  function onPickItem(it: PickerItem) {
    setAdjust((a) => ({
      ...a,
      itemId: it.id,
      itemName: it.name,
      unitPrice: a.unitPrice || String(it.unitPrice ?? ''),
    }))
  }

  // ----- rendu
  if (!id) return <div className='p-4'>Identifiant invalide.</div>
  if (status === 'pending') return <Loading />
  if (!container) return <div className='p-4'>Conteneur introuvable.</div>

  return (
    <div className='space-y-6'>
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to='/containers'>Conteneurs</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{container.name}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className='flex items-center flex-col gap-y-4 sm:flex-row justify-between'>
        <h2 className='text-2xl font-semibold'>
          {container.name}{' '}
          <span className='text-sm text-muted-foreground'>
            #{container.id.slice(0, 8)}
          </span>
        </h2>

        <div className='flex gap-2'>
          <Button
            variant='outline'
            onClick={() => openAdjust({ direction: 'IN' })}
          >
            Stocker
          </Button>
          <Button
            variant='outline'
            onClick={() => openAdjust({ direction: 'OUT' })}
          >
            Destocker
          </Button>
        </div>
      </div>

      <div className='grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'>
        <Info label='Dépôt' value={container.depot?.name ?? '—'} />
        <Info label='Par défaut' value={container.isDefault ? 'Oui' : 'Non'} />
        <Info label='Code' value={container.code ?? '—'} />
        <Info label='Description' value={container.description ?? '—'} />
      </div>

      <div className='rounded-lg border p-3 w-fit'>
        <div className='text-xs text-muted-foreground'>Total</div>
        <div className='mt-1 font-semibold tabular-nums'>{totalBal}</div>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as any)}>
        <TabsList>
          <TabsTrigger value='inventory'>Inventaire</TabsTrigger>
          <TabsTrigger value='moves'>Flux</TabsTrigger>
        </TabsList>

        <TabsContent value='inventory' className='mt-4'>
          <div className='text-sm text-muted-foreground mb-2'>
            Lignes: <b>{invRows.length}</b>
          </div>
          <DataTable<InventoryLevel, unknown>
            columns={invCols}
            data={invRows}
          />
        </TabsContent>

        <TabsContent value='moves' className='mt-4'>
          <DataTable<MoveRow, unknown> columns={moveCols} data={moveRows} />
          {!moveRows.length ? (
            <div className='text-sm text-muted-foreground mt-2'>
              Aucun flux disponible pour ce conteneur.
            </div>
          ) : null}
        </TabsContent>
      </Tabs>

      {/* Modale AJUSTEMENT */}
      <AppDialog
        open={adjustOpen}
        onOpenChange={setAdjustOpen}
        title={adjust.direction === 'OUT' ? 'Destocker' : 'Stocker'}
        description={
          adjust.direction === 'OUT'
            ? 'Enregistrez un mouvement de destockage pour ce conteneur.'
            : 'Enregistrez un mouvement de stockage pour ce conteneur.'
        }
        asForm
        onSubmit={(e) => {
          e.preventDefault()
          submitAdjust()
        }}
        footer={
          <ModalActions
            onConfirm={submitAdjust}
            confirmLabel='Valider'
            confirmLoading={adjustMut.isPending}
          />
        }
      >
        <div className='w-full px-4 py-2 space-y-3'>
          <Field label='Article'>
            <ItemPicker
              value={
                adjust.itemId
                  ? { id: adjust.itemId, name: adjust.itemName ?? '' }
                  : null
              }
              onChange={onPickItem}
              autoCreateUnitPrice={0} // option: PU par défaut si on crée
            />
          </Field>

          <Field label='Quantité'>
            <Input
              inputMode='numeric'
              value={adjust.quantity}
              onChange={(e) =>
                setAdjust((a) => ({
                  ...a,
                  quantity: e.target.value.replace(/[^\d]/g, ''),
                }))
              }
              placeholder='ex: 10'
            />
          </Field>

          <Field label='Prix unitaire (optionnel)'>
            <Input
              inputMode='decimal'
              value={adjust.unitPrice}
              onChange={(e) =>
                setAdjust((a) => ({ ...a, unitPrice: e.target.value }))
              }
              placeholder='ex: 1000.00'
            />
          </Field>

          <Field label='Note (optionnel)'>
            <Input
              value={adjust.note}
              onChange={(e) =>
                setAdjust((a) => ({ ...a, note: e.target.value }))
              }
              placeholder='Motif / commentaire'
            />
          </Field>
        </div>
      </AppDialog>
    </div>
  )
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className='rounded-lg border p-3'>
      <div className='text-xs text-muted-foreground'>{label}</div>
      <div className='mt-1 font-medium break-all'>{value}</div>
    </div>
  )
}

function Field({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div className='grid gap-1.5'>
      <Label>{label}</Label>
      {children}
    </div>
  )
}
